'use client';

import Image from 'next/image';
import React, { useActionState } from 'react';
import { useFormStatus } from 'react-dom';
import Form from 'next/form';
import Link from 'next/link';

import PoweredBySerefin from '../components/shared/PoweredBySerefin';
import AnimatedGradientButton from '../components/shared/AnimatedGradientButton';
import { resendCodeAction, signInAction, verifyMFAAction } from './actions';
import styles from './styles.module.css';
import CopyrightNotice from '@/app/components/shared/CopyrightNotice';

interface SignInFormState {
  success: boolean;
  error?: string;
  requiresMFA?: boolean;
  session?: string;
}

interface MFAFormState {
  success: boolean;
  error?: string;
}

// Component for handling loading state in forms
function SubmitButton({
  children,
  disabled,
}: {
  children: React.ReactNode;
  disabled?: boolean;
}) {
  const { pending } = useFormStatus();

  return (
    <AnimatedGradientButton
      as="button"
      type="submit"
      disabled={disabled || pending}
      text={children as string}
    />
  );
}

export default function SignInForm({ callbackUrl }: { callbackUrl?: string }) {
  const [step, setStep] = React.useState<number>(0);
  const [session, setSession] = React.useState<string>('');
  const [username, setUsername] = React.useState<string>('');

  // Wrapper function for sign-in action that handles step transitions
  const handleSignInWrapper = async (
    prevState: SignInFormState,
    formData: FormData
  ): Promise<SignInFormState> => {
    const result = await signInAction(formData);

    if (!result.success) {
      return {
        error: result.error || 'Sign in failed',
        success: false,
      };
    }

    if (result.requiresMFA) {
      setSession(result.session || '');
      setUsername(formData.get('username') as string);
      setStep(1);
      return {
        requiresMFA: true,
        session: result.session,
        success: true,
      };
    }

    // If successful without MFA, the server action will redirect
    return { success: true };
  };

  // Wrapper function for MFA verification action
  const handleMFAVerificationWrapper = async (
    prevState: MFAFormState,
    formData: FormData
  ): Promise<MFAFormState> => {
    if (!session || !username) {
      return {
        error: 'Invalid session. Please try signing in again.',
        success: false,
      };
    }

    const result = await verifyMFAAction(formData, session, username);

    if (!result.success) {
      return {
        error: result.error || 'MFA verification failed',
        success: false,
      };
    }

    // If successful, the server action will redirect
    return { success: true };
  };

  const [signInState, signInFormAction] = useActionState<
    SignInFormState,
    FormData
  >(handleSignInWrapper, { success: false });

  const [mfaState, mfaFormAction] = useActionState<MFAFormState, FormData>(
    handleMFAVerificationWrapper,
    { success: false }
  );

  const handleResendCode = async (): Promise<void> => {
    if (!username) {
      // Could potentially show this error in UI state, but for now just log
      console.warn('Username not available for resend code');
      return;
    }

    const result = await resendCodeAction(username);

    if (!result.success) {
      // Could potentially show this error in UI state, but for now just log
      console.warn('Failed to resend code:', result.error);
    }
  };

  const renderStepOne = (): React.ReactElement => (
    <Form action={signInFormAction} className="flex flex-col gap-6">
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <label
            className="text-sm font-medium text-gray-700"
            htmlFor="username"
          >
            Email
          </label>
          <input
            className="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
            id="username"
            name="username"
            placeholder="Enter your email"
            required
            type="email"
          />
        </div>

        <div className="flex flex-col gap-2">
          <label
            className="text-sm font-medium text-gray-700"
            htmlFor="password"
          >
            Password
          </label>
          <input
            className="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
            id="password"
            name="password"
            placeholder="Enter your password"
            required
            type="password"
          />
        </div>
      </div>

      {callbackUrl && (
        <input name="callbackUrl" type="hidden" value={callbackUrl} />
      )}

      <SubmitButton>Sign In</SubmitButton>

      <div className="text-center">
        <Link
          className="text-sm text-blue-600 hover:text-blue-800"
          href="/forgot-password"
        >
          Forgot your password?
        </Link>
      </div>
    </Form>
  );

  const renderStepTwo = (): React.ReactElement => (
    <div className="flex flex-col gap-6">
      <div>
        <p className="text-sm text-gray-600">
          We've sent a verification code to your email. Please enter it below.
        </p>
      </div>

      <Form action={mfaFormAction} className="flex flex-col gap-6">
        <div className="flex flex-col gap-2">
          <label
            className="text-sm font-medium text-gray-700"
            htmlFor="confirmationCode"
          >
            Verification Code
          </label>
          <input
            className="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
            id="confirmationCode"
            name="confirmationCode"
            placeholder="Enter verification code"
            required
            type="text"
          />
        </div>

        {callbackUrl && (
          <input name="callbackUrl" type="hidden" value={callbackUrl} />
        )}

        <SubmitButton>Verify Code</SubmitButton>
      </Form>

      <div className="text-center">
        <button
          className="text-sm text-blue-600 hover:text-blue-800"
          onClick={handleResendCode}
          type="button"
        >
          Resend Code
        </button>
      </div>
    </div>
  );

  return (
    <>
      <div className="flex min-h-screen grow">
        <div className="flex grow items-center justify-center">
          <div className="grid h-full w-full grid-cols-1 md:grid-cols-2">
            {/* Form Section */}
            <div className="flex w-full flex-col p-6 sm:max-w-[600px] sm:justify-self-end md:p-9">
              <Link href="/">
                <Image
                  src="/trustiLogoGradient.svg"
                  className="mb-20"
                  height={38}
                  width={110}
                  alt=""
                />
              </Link>
              <h1 className={`pageHeading ${styles.heading}`}>
                {step === 0 && 'Sign In'}
                {step === 1 && 'Verification'}
              </h1>

              {(signInState.error || mfaState.error) && (
                <div className="mb-6 rounded-lg bg-red-50 p-4">
                  <p className="text-sm text-red-700">
                    {step === 0 ? signInState.error : mfaState.error}
                  </p>
                </div>
              )}

              {step === 0 ? renderStepOne() : renderStepTwo()}

              <div className="mt-8 text-center">
                <p className="text-sm text-gray-600">
                  Don't have an account?{' '}
                  <Link
                    className="text-blue-600 hover:text-blue-800"
                    href="/sign-up"
                  >
                    Sign up
                  </Link>
                </p>
              </div>
            </div>

            {/* Decorative Section (Hidden on Mobile) */}
            <div
              className={`${styles.gradientBackground} flex w-full justify-start px-6 pt-6 pb-16 md:p-9`}
            >
              <div className="flex w-full max-w-md flex-col justify-self-end">
                <PoweredBySerefin
                  className="hidden sm:block sm:self-end"
                  isWhite
                />
                <div className={`${styles.signUpContainer} sm:hidden`}>
                  <h3>Don't have an account?</h3>
                  <Link href="/sign-up">Sign Up.</Link>
                </div>
                <div className={styles.serefinContainer}>
                  <PoweredBySerefin />
                  <CopyrightNotice className="uppercase" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
